import { secureGet, secureSet } from '../utils/encryptionUtils';
import { getTodayDate, toISOString, getCurrentSession, getDayOfWeek } from '../utils/dateUtils';
// IDs are generated as R001/B001/etc. — uuid not needed here

// ─── Storage Keys ────────────────────────────────────────────────────
const K = {
  KNOWLEDGE:   'jarvis_knowledge',
  CHUNKS:      'jarvis_knowledge_chunks',
  RULES:       'jarvis_rules',
  BEHAVIORS:   'jarvis_behaviors',
  TRADES:      'jarvis_trades',
  PATTERNS:    'jarvis_patterns',
  ICT:         'jarvis_ict',
  DAILY:       'jarvis_daily',
  PSYCHOLOGY:  'jarvis_psychology',
  PREDICTIONS: 'jarvis_predictions',
  RISK:        'jarvis_risk',
  MEMORY:      'jarvis_memory',
  PRICE:       'jarvis_price',
  SESSION:     'jarvis_session_learning',
};

// ─── Clear LEGACY trading memory ────────────────────────────────────────
// Wipes everything that was built up from the old screenshot+Groq-vision
// analysis (unreliable — confirmed by direct use, see jarvisDNA.js →
// trading.validated_strategy comments). Does NOT touch: RISK (Sir's risk
// settings), DAILY (personal teaching log), BEHAVIORS/MEMORY (general
// assistant memory/evolution stage), ICT (concept reference library) —
// those aren't tied to the bad vision predictions and are safe to keep.
export const clearLegacyTradingMemory = () => {
  [K.KNOWLEDGE, K.CHUNKS, K.RULES, K.TRADES, K.PATTERNS, K.PREDICTIONS, K.SESSION]
    .forEach(key => secureSet(key, []));
  localStorage.removeItem('jarvis_vision_history');   // ChartVision.jsx history (raw key, not via K)
  return { ok: true, cleared: ['KNOWLEDGE', 'CHUNKS', 'RULES', 'TRADES', 'PATTERNS', 'PREDICTIONS', 'SESSION', 'vision_history'] };
};

// ─── Knowledge Base ──────────────────────────────────────────────────
export const getKnowledge = () => secureGet(K.KNOWLEDGE, []);

export const saveKnowledge = (item) => {
  const list = getKnowledge();
  const newItem = {
    id: `K${String(list.length + 1).padStart(3, '0')}`,
    type: item.type || 'personal',
    source: item.source || 'Manual',
    title: item.title || '',
    raw_content: item.raw_content || '',
    extracted_rules: [],
    extracted_behaviors: [],
    extracted_concepts: [],
    conflicts_with: [],
    confidence: item.confidence || 80,
    times_referenced: 0,
    date_added: toISOString(),
    last_referenced: null,
  };
  secureSet(K.KNOWLEDGE, [...list, newItem]);
  return newItem;
};

export const updateKnowledge = (id, updates) => {
  const list = getKnowledge();
  secureSet(K.KNOWLEDGE, list.map(k => k.id === id ? { ...k, ...updates } : k));
};

export const deleteKnowledge = (id) => {
  secureSet(K.KNOWLEDGE, getKnowledge().filter(k => k.id !== id));
};

// Seed a script only once — checks seed_id to prevent duplicates
export const seedKnowledgeScript = (item) => {
  const list = getKnowledge();
  if (list.some(k => k.seed_id === item.seed_id)) return; // already seeded
  secureSet(K.KNOWLEDGE, [...list, {
    id:               `K${String(list.length + 1).padStart(3, '0')}`,
    seed_id:          item.seed_id,
    type:             'script',
    source:           item.platform || 'TradingView',
    title:            item.title,
    raw_content:      item.code,
    category:         item.category || 'Script',
    description:      item.description || '',
    marathi_desc:     item.marathi_desc || '',
    usage_steps:      item.usage_steps || [],
    extracted_rules:  [],
    extracted_behaviors: [],
    extracted_concepts:  [],
    conflicts_with:   [],
    confidence:       100,
    times_referenced: 0,
    date_added:       toISOString(),
    last_referenced:  null,
  }]);
};

// ─── Knowledge Chunks (RAG — structured transcript extractions) ──────
// Each chunk = one concept/rule/example/mistake/checklist item from a source.
export const getKnowledgeChunks = () => secureGet(K.CHUNKS, []);

export const saveKnowledgeChunk = (chunk) => {
  const list = getKnowledgeChunks();
  const newChunk = {
    id:         `CH${String(list.length + 1).padStart(4, '0')}`,
    source:     chunk.source   || 'Unknown',
    category:   chunk.category || 'concept', // concept | rule | example | mistake | checklist
    title:      chunk.title    || '',
    content:    chunk.content  || '',
    tags:       chunk.tags     || [],
    created_at: toISOString(),
  };
  secureSet(K.CHUNKS, [...list, newChunk]);
  return newChunk;
};

// Keyword search — returns topN most relevant chunks for a query
export const searchKnowledgeChunks = (query, topN = 5) => {
  if (!query?.trim()) return [];
  const chunks = getKnowledgeChunks();
  if (!chunks.length) return [];
  const words = query.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  if (!words.length) return [];
  return chunks
    .map(c => {
      const text = `${c.title} ${c.content} ${(c.tags || []).join(' ')}`.toLowerCase();
      const score = words.filter(w => text.includes(w)).length;
      return { ...c, score };
    })
    .filter(c => c.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, topN);
};

export const deleteKnowledgeChunk = (id) => {
  secureSet(K.CHUNKS, getKnowledgeChunks().filter(c => c.id !== id));
};

// Auto-sync: knowledge chunks → Rule Book + Behaviors
// category:"rule" → saveRule(), category:"concept"/"mistake" → saveBehavior()
// Returns { rulesAdded, behaviorsAdded } counts
export const syncChunksToRules = () => {
  const chunks    = getKnowledgeChunks();
  const rules     = getRules();
  const behaviors = getBehaviors();

  let rulesAdded     = 0;
  let behaviorsAdded = 0;

  for (const chunk of chunks) {
    const seedId = `chunk_${chunk.id}`;

    if (chunk.category === 'rule' || chunk.category === 'checklist') {
      // Skip if already synced
      if (rules.some(r => r.seed_id === seedId)) continue;

      const rules_list = getRules();
      const newRule = {
        id:             `R${String(rules_list.length + 1).padStart(3, '0')}`,
        seed_id:        seedId,
        title:          chunk.title || 'Untitled Rule',
        condition:      chunk.content || '',
        action:         'Apply as per rule',
        source:         chunk.source || 'Knowledge Base',
        notes:          (chunk.tags || []).join(', '),
        fib_level:      null,
        ict_concept:    null,
        session:        'Any',
        htf_bias:       'Any',
        sl_method:      null,
        tp_method:      null,
        rr_minimum:     2,
        win_count:      0,
        total_count:    0,
        win_rate:       0,
        confidence:     'UNTESTED',
        last_result:    null,
        version:        1,
        evolution_history: [],
        conflicts_with: [],
        status:         'active',
        retirement_reason: null,
        auto_extracted: true,
        date_created:   toISOString(),
        last_updated:   toISOString(),
      };
      secureSet(K.RULES, [...getRules(), newRule]);
      rulesAdded++;

    } else if (chunk.category === 'concept' || chunk.category === 'mistake') {
      // Skip if already synced
      if (behaviors.some(b => b.seed_id === seedId)) continue;

      const beh_list = getBehaviors();
      const newBehavior = {
        id:              `B${String(beh_list.length + 1).padStart(3, '0')}`,
        seed_id:         seedId,
        title:           chunk.title || 'Unnamed',
        category:        chunk.category === 'mistake' ? 'mistake' : 'concept',
        description:     chunk.content || '',
        conditions:      chunk.tags || [],
        typical_outcome: '',
        observed_count:  1,
        correct_count:   0,
        accuracy:        0,
        confidence:      'LOW',
        last_seen:       getTodayDate(),
        date_discovered: toISOString(),
        learned_from:    `JSON Import — ${chunk.source || 'Knowledge Base'}`,
      };
      secureSet(K.BEHAVIORS, [...getBehaviors(), newBehavior]);
      behaviorsAdded++;
    }
  }

  return { rulesAdded, behaviorsAdded };
};

// ─── Rules ───────────────────────────────────────────────────────────
export const getRules = () => secureGet(K.RULES, []);

export const saveRule = (rule) => {
  const rules = getRules();
  const newRule = {
    id: `R${String(rules.length + 1).padStart(3, '0')}`,
    title: rule.title || 'Untitled Rule',
    fib_level: rule.fib_level || null,
    ict_concept: rule.ict_concept || null,
    condition: rule.condition || '',
    action: rule.action || 'WAIT',
    session: rule.session || 'Any',
    htf_bias: rule.htf_bias || 'Any',
    sl_method: rule.sl_method || null,
    tp_method: rule.tp_method || null,
    rr_minimum: rule.rr_minimum || 2,
    source: rule.source || 'Manual',
    notes: rule.notes || '',
    // Performance
    win_count: 0,
    total_count: 0,
    win_rate: 0,
    confidence: 'UNTESTED',
    last_result: null,
    // Metadata
    version: 1,
    evolution_history: [],
    conflicts_with: [],
    status: 'active',
    retirement_reason: null,
    auto_extracted: rule.auto_extracted || false,
    date_created: toISOString(),
    last_updated: toISOString(),
  };
  secureSet(K.RULES, [...rules, newRule]);
  return newRule;
};

// Seed rules only once — checks seed_id to prevent duplicates on every app load
export const seedRule = (rule) => {
  const rules = getRules();
  if (rules.some(r => r.seed_id === rule.seed_id)) return; // already seeded
  const newRule = {
    id:               `R${String(rules.length + 1).padStart(3, '0')}`,
    seed_id:          rule.seed_id,
    title:            rule.title || 'Untitled Rule',
    fib_level:        rule.fib_level || null,
    ict_concept:      rule.ict_concept || null,
    condition:        rule.condition || '',
    action:           rule.action || 'WAIT',
    session:          rule.session || 'Any',
    htf_bias:         rule.htf_bias || 'Any',
    sl_method:        rule.sl_method || null,
    tp_method:        rule.tp_method || null,
    rr_minimum:       rule.rr_minimum || 2,
    source:           rule.source || 'Pine Script',
    notes:            rule.notes || '',
    win_count:        0,
    total_count:      0,
    win_rate:         0,
    confidence:       'UNTESTED',
    last_result:      null,
    version:          1,
    evolution_history: [],
    conflicts_with:   [],
    status:           'active',
    retirement_reason: null,
    auto_extracted:   true,
    date_created:     toISOString(),
    last_updated:     toISOString(),
  };
  secureSet(K.RULES, [...rules, newRule]);
  return newRule;
};

export const updateRule = (id, updates) => {
  const rules = getRules();
  secureSet(K.RULES, rules.map(r => r.id === id ? { ...r, ...updates, last_updated: toISOString() } : r));
};

export const updateRuleResult = (ruleId, won) => {
  const rules = getRules();
  secureSet(K.RULES, rules.map(r => {
    if (r.id !== ruleId) return r;

    const newTotal = r.total_count + 1;
    const newWins  = won ? r.win_count + 1 : r.win_count;
    const newRate  = Math.round((newWins / newTotal) * 100);

    const confidence =
      newTotal < 5  ? 'UNTESTED' :
      newRate >= 70 ? 'HIGH'     :
      newRate >= 55 ? 'MEDIUM'   : 'LOW';

    // Log evolution only when win rate changes
    const didChange = newRate !== r.win_rate;
    const evolution = didChange ? [
      ...r.evolution_history,
      { version: r.version + 1, win_rate: newRate, date: getTodayDate(),
        change: won ? '+1 WIN' : '+1 LOSS' }
    ] : r.evolution_history;

    return {
      ...r,
      win_count: newWins,
      total_count: newTotal,
      win_rate: newRate,
      confidence,
      last_result: won ? 'WIN' : 'LOSS',
      version: didChange ? r.version + 1 : r.version,
      evolution_history: evolution,
      // Auto-flag for review if win rate drops below 40% after 20+ trades
      status: (newTotal >= 20 && newRate < 40) ? 'under_review' : r.status,
      last_updated: toISOString(),
    };
  }));
};

export const retireRule = (id, reason = '') => {
  updateRule(id, { status: 'retired', retirement_reason: reason });
};

export const deleteRule = (id) => {
  secureSet(K.RULES, getRules().filter(r => r.id !== id));
};

// ─── Behaviors ───────────────────────────────────────────────────────
export const getBehaviors = () => secureGet(K.BEHAVIORS, []);

export const saveBehavior = (behavior) => {
  const list = getBehaviors();

  // Deduplicate: if very similar title exists, increment count instead
  const existing = list.find(b =>
    b.title && behavior.title &&
    b.title.toLowerCase().substring(0, 15) === behavior.title.toLowerCase().substring(0, 15)
  );
  if (existing) {
    updateBehavior(existing.id, {
      observed_count: existing.observed_count + 1,
      last_seen: getTodayDate(),
    });
    return existing;
  }

  const newBehavior = {
    id: `B${String(list.length + 1).padStart(3, '0')}`,
    title: behavior.title || 'Unnamed Behavior',
    category: behavior.category || 'general',
    subcategory: behavior.subcategory || null,
    description: behavior.description || '',
    conditions: behavior.conditions || [],
    typical_outcome: behavior.typical_outcome || '',
    observed_count: 1,
    correct_count: 0,
    accuracy: 0,
    confidence: 'LOW',      // grows: LOW → MEDIUM → HIGH → CONFIRMED
    last_seen: getTodayDate(),
    date_discovered: toISOString(),
    learned_from: behavior.learned_from || 'Chat discussion',
  };
  secureSet(K.BEHAVIORS, [...list, newBehavior]);
  return newBehavior;
};

export const updateBehavior = (id, updates) => {
  const list = getBehaviors();
  secureSet(K.BEHAVIORS, list.map(b => {
    if (b.id !== id) return b;
    const updated = { ...b, ...updates };
    // Auto-update confidence level from observed count
    updated.confidence =
      updated.observed_count < 5  ? 'LOW'       :
      updated.observed_count < 15 ? 'MEDIUM'    :
      updated.observed_count < 25 ? 'HIGH'      : 'CONFIRMED';
    return updated;
  }));
};

export const confirmBehavior = (id, wasCorrect) => {
  const list = getBehaviors();
  secureSet(K.BEHAVIORS, list.map(b => {
    if (b.id !== id) return b;
    const newCorrect = wasCorrect ? b.correct_count + 1 : b.correct_count;
    const newObs     = b.observed_count + 1;
    return {
      ...b,
      observed_count: newObs,
      correct_count: newCorrect,
      accuracy: Math.round((newCorrect / newObs) * 100),
      last_seen: getTodayDate(),
    };
  }));
};

export const deleteBehavior = (id) => {
  secureSet(K.BEHAVIORS, getBehaviors().filter(b => b.id !== id));
};

// ─── Daily Lessons (The Core Teaching System) ────────────────────────
export const getDailyLessons = () => secureGet(K.DAILY, []);

export const saveDailyLesson = (lesson) => {
  const list = getDailyLessons();
  const today = getTodayDate();
  const existingIdx = list.findIndex(d => d.date === today);

  const entry = {
    id: existingIdx >= 0 ? list[existingIdx].id : `DL${String(list.length + 1).padStart(3, '0')}`,
    date: today,
    day_of_week: getDayOfWeek(),
    session: getCurrentSession().name,
    // Lesson fields
    market_behavior:    lesson.market_behavior    || '',
    key_levels:         lesson.key_levels         || '',
    patterns_observed:  lesson.patterns_observed  || '',
    what_worked:        lesson.what_worked        || '',
    what_failed:        lesson.what_failed        || '',
    ict_observations:   lesson.ict_observations   || '',
    trades_count:       lesson.trades_count       || 0,
    overall_result:     lesson.overall_result     || 'NEUTRAL',
    psychology_rating:  lesson.psychology_rating  || 5,
    notes:              lesson.notes              || '',
    // JARVIS analysis (filled after processing)
    jarvis_summary:     lesson.jarvis_summary     || null,
    jarvis_takeaway:    lesson.jarvis_takeaway    || null,
    behaviors_extracted: lesson.behaviors_extracted || [],
    patterns_extracted:  lesson.patterns_extracted  || [],
    tomorrow_watch:      lesson.tomorrow_watch      || null,
    last_updated: toISOString(),
  };

  if (existingIdx >= 0) {
    const updated = [...list];
    updated[existingIdx] = { ...list[existingIdx], ...entry };
    secureSet(K.DAILY, updated);
  } else {
    secureSet(K.DAILY, [...list, entry]);
  }
  return entry;
};

export const getTodayLesson = () => {
  return getDailyLessons().find(d => d.date === getTodayDate()) || null;
};

export const getRecentLessons = (days = 7) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffStr = cutoff.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  return getDailyLessons()
    .filter(d => d.date >= cutoffStr)
    .sort((a, b) => b.date.localeCompare(a.date));
};

// ─── Trades ───────────────────────────────────────────────────────────
export const getTrades = () => secureGet(K.TRADES, []);

export const saveTrade = (trade) => {
  const trades = getTrades();
  const session = getCurrentSession();
  const newTrade = {
    id: `T${String(trades.length + 1).padStart(3, '0')}`,
    date: getTodayDate(),
    time_ist: new Date().toLocaleTimeString('en-IN'),
    day_of_week: getDayOfWeek(),
    session: session.name,
    // Setup
    direction:         trade.direction        || 'BUY',
    setup_type:        trade.setup_type       || '',
    ict_concept:       trade.ict_concept      || '',
    htf_bias:          trade.htf_bias         || '',
    confluence_score:  trade.confluence_score || 0,
    setup_grade:       trade.setup_grade      || 'C',
    // Prices
    entry:   parseFloat(trade.entry)   || 0,
    sl:      parseFloat(trade.sl)      || 0,
    tp1:     parseFloat(trade.tp1)     || 0,
    tp2:     parseFloat(trade.tp2)     || 0,
    lot_size: parseFloat(trade.lot_size) || 0.01,
    rr_planned: parseFloat(trade.rr_planned) || 0,
    // Result (filled later)
    result:       trade.result       || 'OPEN',
    exit_price:   trade.exit_price   || null,
    pips:         trade.pips         || null,
    rr_achieved:  trade.rr_achieved  || null,
    pnl_usd:      trade.pnl_usd      || null,
    // Psychology
    psychology_before: trade.psychology_before || 5,
    psychology_after:  trade.psychology_after  || null,
    followed_rules:    trade.followed_rules    || true,
    rules_violated:    trade.rules_violated    || [],
    // Tags
    rules_used:         trade.rules_used        || [],
    behaviors_matched:  trade.behaviors_matched || [],
    // JARVIS
    jarvis_suggested:   trade.jarvis_suggested  || false,
    jarvis_confidence:  trade.jarvis_confidence || null,
    jarvis_was_correct: null,
    // Signal context at the moment JARVIS logged this — needed for
    // self-learning (win-rate by ADX bucket / trend / HTF alignment /
    // timeframe). Only populated for JARVIS's own auto-logged signals
    // (scriptAnalysis.js's autoLogSignalAsTrade) — stays null for
    // manually-entered trades, which is correct: there's no "signal" to
    // attribute context to for those.
    timeframe:        trade.timeframe        || null,
    adx_at_signal:    trade.adx_at_signal    ?? null,
    trend_at_signal:  trade.trend_at_signal  || null,
    htf_aligned:      trade.htf_aligned      ?? null,
    // Notes
    notes:             trade.notes              || '',
    lessons_learned:   trade.lessons_learned    || '',
    screenshot_entry:  trade.screenshot_entry   || null,
    date_logged: toISOString(),
  };
  secureSet(K.TRADES, [...trades, newTrade]);
  return newTrade;
};

export const updateTradeResult = (tradeId, result) => {
  const trades = getTrades();
  const trade = trades.find(t => t.id === tradeId);

  secureSet(K.TRADES, trades.map(t =>
    t.id === tradeId
      ? { ...t, ...result, date_closed: toISOString() }
      : t
  ));

  // Cascade: update win rates on rules used
  if (trade?.rules_used?.length && result.result && result.result !== 'OPEN') {
    const won = result.result === 'WIN';
    trade.rules_used.forEach(rId => updateRuleResult(rId, won));
  }
};

export const deleteTrade = (id) => {
  secureSet(K.TRADES, getTrades().filter(t => t.id !== id));
};

// ─── Patterns ─────────────────────────────────────────────────────────
export const getPatterns = () => secureGet(K.PATTERNS, []);

export const savePattern = (pattern) => {
  const list = getPatterns();
  const newPattern = {
    id: `P${String(list.length + 1).padStart(3, '0')}`,
    title: pattern.title || 'Unnamed Pattern',
    category: pattern.category || 'entry',
    description: pattern.description || '',
    conditions: pattern.conditions || [],
    best_session: pattern.best_session || 'Any',
    best_day: pattern.best_day || 'Any',
    total_occurrences: 1,
    win_count: 0,
    win_rate: 0,
    confidence: 'EMERGING',   // EMERGING → DEVELOPING → STRONG → CONFIRMED
    auto_discovered: pattern.auto_discovered || false,
    trades_using_this: [],
    date_discovered: toISOString(),
    last_seen: getTodayDate(),
  };
  secureSet(K.PATTERNS, [...list, newPattern]);
  return newPattern;
};

export const updatePatternResult = (id, won) => {
  const list = getPatterns();
  secureSet(K.PATTERNS, list.map(p => {
    if (p.id !== id) return p;
    const newTotal = p.total_occurrences + 1;
    const newWins  = won ? p.win_count + 1 : p.win_count;
    const newRate  = Math.round((newWins / newTotal) * 100);
    return {
      ...p,
      total_occurrences: newTotal,
      win_count: newWins,
      win_rate: newRate,
      confidence:
        newTotal < 5  ? 'EMERGING'   :
        newTotal < 15 ? 'DEVELOPING' :
        newRate >= 70 ? 'STRONG'     : 'WEAK',
      last_seen: getTodayDate(),
    };
  }));
};

export const deletePattern = (id) => {
  secureSet(K.PATTERNS, getPatterns().filter(p => p.id !== id));
};

// ─── ICT / SMC Concepts ───────────────────────────────────────────────
const ICT_DEFAULT = { order_blocks: [], fvg: [], liquidity_levels: [], market_structure: [], power_of_3: [] };

export const getICT = () => secureGet(K.ICT, ICT_DEFAULT);

export const saveOrderBlock = (ob) => {
  const ict = getICT();
  const newOB = {
    id: `OB${String(ict.order_blocks.length + 1).padStart(3, '0')}`,
    type:        ob.type        || 'Bullish',
    timeframe:   ob.timeframe   || 'H1',
    top:    parseFloat(ob.top)  || 0,
    bottom: parseFloat(ob.bottom) || 0,
    status: 'Active',
    tested_count: 0,
    notes:       ob.notes       || '',
    date_formed: getTodayDate(),
  };
  secureSet(K.ICT, { ...ict, order_blocks: [...ict.order_blocks, newOB] });
  return newOB;
};

export const saveFVG = (fvg) => {
  const ict = getICT();
  const newFVG = {
    id: `FVG${String(ict.fvg.length + 1).padStart(3, '0')}`,
    type:      fvg.type      || 'Bullish',
    timeframe: fvg.timeframe || 'H1',
    top:    parseFloat(fvg.top)    || 0,
    bottom: parseFloat(fvg.bottom) || 0,
    status: 'Open',
    notes:     fvg.notes     || '',
    date_formed: getTodayDate(),
  };
  secureSet(K.ICT, { ...ict, fvg: [...ict.fvg, newFVG] });
  return newFVG;
};

export const saveLiquidityLevel = (level) => {
  const ict = getICT();
  const newLiq = {
    id: `LIQ${String(ict.liquidity_levels.length + 1).padStart(3, '0')}`,
    type:      level.type      || 'BSL',
    price: parseFloat(level.price) || 0,
    timeframe: level.timeframe || 'H4',
    status: 'Intact',
    notes:     level.notes     || '',
    date_identified: getTodayDate(),
  };
  secureSet(K.ICT, { ...ict, liquidity_levels: [...ict.liquidity_levels, newLiq] });
  return newLiq;
};

export const saveMarketStructure = (event) => {
  const ict = getICT();
  const newMS = {
    id: `MS${String(ict.market_structure.length + 1).padStart(3, '0')}`,
    event:     event.event     || 'BOS',
    direction: event.direction || 'Bullish',
    timeframe: event.timeframe || 'H1',
    price: parseFloat(event.price) || 0,
    notes:     event.notes     || '',
    date: getTodayDate(),
  };
  secureSet(K.ICT, { ...ict, market_structure: [...ict.market_structure, newMS] });
  return newMS;
};

export const updateICTStatus = (type, id, status) => {
  const ict = getICT();
  secureSet(K.ICT, {
    ...ict,
    [type]: ict[type].map(item => item.id === id ? { ...item, status } : item),
  });
};

export const deleteICT = (type, id) => {
  const ict = getICT();
  secureSet(K.ICT, { ...ict, [type]: ict[type].filter(item => item.id !== id) });
};

// ─── Psychology Logs ──────────────────────────────────────────────────
export const getPsychologyLogs = () => secureGet(K.PSYCHOLOGY, []);

export const savePsychologyLog = (log) => {
  const list = getPsychologyLogs();
  const today = getTodayDate();
  const existingIdx = list.findIndex(p => p.date === today);
  const entry = { date: today, ...log, logged_at: toISOString() };

  if (existingIdx >= 0) {
    const updated = [...list];
    updated[existingIdx] = { ...list[existingIdx], ...entry };
    secureSet(K.PSYCHOLOGY, updated);
  } else {
    secureSet(K.PSYCHOLOGY, [...list, entry]);
  }
  return entry;
};

// ─── JARVIS Prediction Ledger ─────────────────────────────────────────
export const getPredictions = () => secureGet(K.PREDICTIONS, []);

export const savePrediction = (prediction) => {
  const list = getPredictions();
  const newPred = {
    id: `PRED${String(list.length + 1).padStart(3, '0')}`,
    date: getTodayDate(),
    time_ist: new Date().toLocaleTimeString('en-IN'),
    direction:          prediction.direction         || 'WAIT',
    grade:              prediction.grade             || 'C',
    confidence_given:   prediction.confidence_given  || 0,
    price_at_prediction: prediction.price_at_prediction || 0,
    reply_summary:      prediction.reply_summary     || '',
    outcome_recorded:   false,
    was_correct:        null,
    actual_outcome:     null,
    linked_trade:       null,
  };
  secureSet(K.PREDICTIONS, [...list, newPred]);
  return newPred;
};

export const updatePredictionOutcome = (id, outcome) => {
  const list = getPredictions();
  secureSet(K.PREDICTIONS, list.map(p =>
    p.id === id ? { ...p, ...outcome, outcome_recorded: true } : p
  ));
};

// ─── Risk Settings ────────────────────────────────────────────────────
export const getRiskSettings = () => secureGet(K.RISK, {
  account_balance: 1000,
  risk_per_trade_percent: 1,
  max_daily_loss_percent: 3,
  max_weekly_loss_percent: 5,
  max_daily_trades: 3,
});

export const updateRiskSettings = (settings) => {
  secureSet(K.RISK, { ...getRiskSettings(), ...settings });
};

// ─── Live Price ───────────────────────────────────────────────────────
export const getCurrentPrice = () =>
  parseFloat(localStorage.getItem(K.PRICE) || '0');

export const setCurrentPrice = (price) =>
  localStorage.setItem(K.PRICE, String(price));

// ─── JARVIS Self-Memory ───────────────────────────────────────────────
const MEMORY_DEFAULT = {
  total_discussions: 0,
  total_screenshots: 0,
  total_scripts_learned: 0,
  evolution_stage: 1,
  knowledge_score: 0,
  accuracy_history: [],
  self_weaknesses: [],
  self_strengths: [],
  weekly_briefings: [],
  personality_mode: 'Professional',
};

export const getJarvisMemoryData = () => secureGet(K.MEMORY, MEMORY_DEFAULT);

export const updateJarvisMemory = (updates) => {
  secureSet(K.MEMORY, { ...getJarvisMemoryData(), ...updates });
};

export const incrementDiscussions = () => {
  const m = getJarvisMemoryData();
  updateJarvisMemory({ total_discussions: (m.total_discussions || 0) + 1 });
};

// ─── Stats (calculated, not stored) ──────────────────────────────────
export const getStats = () => {
  const closed = getTrades().filter(t => t.result && t.result !== 'OPEN');
  if (!closed.length) return {
    totalTrades: 0, wins: 0, losses: 0, winRate: 0,
    totalPnL: '0.00', avgRR: '0.00', bySession: {}, byDay: {},
    byGrade: {}, bestDay: 'Not enough data', bestGrade: 'Not enough data',
  };

  const wins   = closed.filter(t => t.result === 'WIN').length;
  const totalPnL = closed.reduce((s, t) => s + (parseFloat(t.pnl_usd) || 0), 0);
  const avgRR    = closed.reduce((s, t) => s + (parseFloat(t.rr_achieved) || 0), 0) / closed.length;

  // By session
  const bySession = {};
  closed.forEach(t => {
    if (!bySession[t.session]) bySession[t.session] = { wins: 0, total: 0 };
    bySession[t.session].total++;
    if (t.result === 'WIN') bySession[t.session].wins++;
  });

  // By day of week
  const byDay = {};
  closed.forEach(t => {
    const day = t.day_of_week || new Date(t.date).toLocaleDateString('en-IN', { weekday: 'long' });
    if (!byDay[day]) byDay[day] = { wins: 0, total: 0 };
    byDay[day].total++;
    if (t.result === 'WIN') byDay[day].wins++;
  });

  // By grade
  const byGrade = {};
  closed.forEach(t => {
    const g = t.setup_grade || 'Unknown';
    if (!byGrade[g]) byGrade[g] = { wins: 0, total: 0 };
    byGrade[g].total++;
    if (t.result === 'WIN') byGrade[g].wins++;
  });

  const bestDay = Object.entries(byDay)
    .filter(([, v]) => v.total >= 2)
    .sort((a, b) => (b[1].wins / b[1].total) - (a[1].wins / a[1].total))[0]?.[0]
    || 'Not enough data';

  const bestGrade = Object.entries(byGrade)
    .filter(([, v]) => v.total >= 2)
    .sort((a, b) => (b[1].wins / b[1].total) - (a[1].wins / a[1].total))[0]?.[0]
    || 'Not enough data';

  return {
    totalTrades: closed.length,
    wins,
    losses: closed.length - wins,
    winRate: Math.round((wins / closed.length) * 100),
    totalPnL: totalPnL.toFixed(2),
    avgRR: avgRR.toFixed(2),
    bySession,
    byDay,
    byGrade,
    bestDay,
    bestGrade,
  };
};

// ─── Full JARVIS Context (passed to system prompt) ────────────────────
export const getFullJarvisContext = () => ({
  knowledge:    getKnowledge(),
  rules:        getRules().filter(r => r.status === 'active'),
  behaviors:    getBehaviors(),
  patterns:     getPatterns(),
  ict:          getICT(),
  daily:        getRecentLessons(7),
  todayLesson:  getTodayLesson(),
  stats:        getStats(),
  riskSettings: getRiskSettings(),
  jarvisMemory: getJarvisMemoryData(),
  currentPrice: getCurrentPrice(),
  currentSession: getCurrentSession(),
});

// ─── Storage Size Monitor ─────────────────────────────────────────────
export const getStorageSizeMB = () => {
  let bytes = 0;
  for (const key in localStorage) {
    if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
      bytes += (localStorage.getItem(key)?.length || 0) * 2;
    }
  }
  return (bytes / 1024 / 1024).toFixed(2);
};

export const isStorageNearLimit = () => parseFloat(getStorageSizeMB()) > 4.0;

// ─── Export / Import ──────────────────────────────────────────────────
export const getExportJSON = () => {
  const strategies = secureGet('jarvis_strategies', []);
  const hypotheses = secureGet('jarvis_hypotheses', []);
  const data = {
    version:      '3.1',
    exported_at:  toISOString(),
    knowledge:    getKnowledge(),
    chunks:       getKnowledgeChunks(),
    rules:        getRules(),
    behaviors:    getBehaviors(),
    patterns:     getPatterns(),
    trades:       getTrades(),
    predictions:  getPredictions(),
    strategies,
    hypotheses,
    ict:          getICT(),
    daily:        getDailyLessons(),
    psychology:   getPsychologyLogs(),
    risk:         getRiskSettings(),
    jarvisMemory: getJarvisMemoryData(),
  };
  return { json: JSON.stringify(data, null, 2), filename: `jarvis-backup-${getTodayDate()}.json` };
};

export const exportAllData = () => {
  const { json, filename } = getExportJSON();
  const blob = new Blob([json], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const importData = (jsonString) => {
  const data = JSON.parse(jsonString);
  // Core knowledge
  if (data.knowledge)    secureSet(K.KNOWLEDGE,   data.knowledge);
  if (data.chunks)       secureSet(K.CHUNKS,       data.chunks);
  if (data.rules)        secureSet(K.RULES,        data.rules);
  if (data.behaviors)    secureSet(K.BEHAVIORS,    data.behaviors);
  if (data.patterns)     secureSet(K.PATTERNS,     data.patterns);
  // Trade history
  if (data.trades)       secureSet(K.TRADES,       data.trades);
  if (data.predictions)  secureSet(K.PREDICTIONS,  data.predictions);
  // Brain self-learned data (jarvisBrain.js SK keys)
  if (data.strategies)   secureSet('jarvis_strategies', data.strategies);
  if (data.hypotheses)   secureSet('jarvis_hypotheses', data.hypotheses);
  // Learning data
  if (data.ict)          secureSet(K.ICT,          data.ict);
  if (data.daily)        secureSet(K.DAILY,        data.daily);
  if (data.psychology)   secureSet(K.PSYCHOLOGY,   data.psychology);
  if (data.risk)         secureSet(K.RISK,         data.risk);
  if (data.jarvisMemory) secureSet(K.MEMORY,       data.jarvisMemory);
  return { success: true, restored: Object.keys(data).filter(k => !['version','exported_at'].includes(k)).length };
};

// alias for SettingsScreen
export const importAllData = importData;

export const clearAllData = () => {
  Object.values(K).forEach(key => localStorage.removeItem(key));
};

// ─── Session Learning (today's live monitor learnings) ────────────────
export const getSessionLearning = () => {
  const all = secureGet(K.SESSION, []);
  const today = getTodayDate();
  return all.filter(i => i.date === today);
};

export const saveSessionLearning = (item) => {
  const all  = secureGet(K.SESSION, []);
  const entry = {
    id:        `SL${Date.now()}`,
    date:      getTodayDate(),
    time:      new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
    timestamp: toISOString(),
    type:      item.type || 'observation',   // 'rule'|'level'|'pattern'|'observation'
    title:     item.title || '',
    content:   item.content || '',
    price:     item.price || null,
    result:    item.result || null,          // 'held'|'rejected'|null
    saved_as_rule: item.saved_as_rule || false,
  };
  all.push(entry);
  // Keep last 200 entries across all days
  const trimmed = all.slice(-200);
  secureSet(K.SESSION, trimmed);
  return entry;
};

export const markSessionLevelResult = (id, result) => {
  const all = secureGet(K.SESSION, []);
  const idx = all.findIndex(i => i.id === id);
  if (idx !== -1) {
    all[idx].result = result;
    secureSet(K.SESSION, all);
  }
};
